/*
 * File: GetCourse.ts
 * Author: Austin Robinson
 * Date Created: 8/5/2023
 * Copyright (c) DrivingDoc 2023
 */
import { AxiosError } from 'axios';
import { SerializedEditorState } from 'lexical';
import { createQuery } from 'react-query-kit';

import { ErrorResponse, MinimalUser, PostGreSQLInterval, privateApi } from 'api';

export interface GetCourseParams {
  id: string;
}

export interface Course {
  tenant_id: string;
  id: string;
  course_type: 'behind_the_wheel' | 'in_person' | 'synchronous_online' | 'fully_online';
  schedule_type: 'self_scheduled' | 'fixed' | 'manual' | 'no_schedule';
  title: string;
  description: string | null;
  created_at: string | null;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
  agreement_id: string | null;
  agreement: {
    id: string;
    tenant_id: string;
    agreement_versions: {
      agreement_id: string;
      version: number;
      tenant_id: string;
      content: SerializedEditorState;
      effective_at: Date;
      created_at: Date;
      created_by?: string | null;
      created_by_user: MinimalUser | null;
    }[];
    created_at: Date;
    created_by?: string | null;
    created_by_user: MinimalUser | null;
  } | null;
  course_policies:
    | {
        tenant_id: string;
        course_id: string;
        updated_at: string;
        min_time_to_complete: PostGreSQLInterval | null;
        max_time_to_complete: PostGreSQLInterval | null;
        standard_extension_time: PostGreSQLInterval | null;
        standard_extension_fee: number | null;
        max_extensions: number | null;
        max_time_to_request_extension: PostGreSQLInterval | null;
        allow_cancellations: boolean | null;
        allowed_absences_before_fee: number | null;
        absence_fee: number | null;
        absence_fee_due: PostGreSQLInterval | null;
        no_permit_fee: number | null;
        no_permit_fee_due: PostGreSQLInterval | null;
        cancellation_fee: number | null;
        cancellation_fee_due: PostGreSQLInterval | null;
        late_cancellation_deadline: PostGreSQLInterval | null;
        late_cancellation_fee: number | null;
        late_cancellation_fee_due: PostGreSQLInterval | null;
        updated_by: {
          tenant_id: string;
          id: string;
          title: string | null;
          first_name: string | null;
          middle_name: string | null;
          last_name: string | null;
          nickname: string | null;
          email: string | null;
          date_of_birth: string | null;
          sex: string | null;
          active: boolean;
          created_at: string | null;
          updated_at: string | null;
          created_by: string | null;
          updated_by: string | null;
          des_id: string | null;
        } | null;
      }[]
    | null;
  course_enrollment_criteria:
    | {
        tenant_id: string;
        course_id: string;
        updated_at: string;
        min_age_to_register: PostGreSQLInterval | null;
        max_age_to_register: PostGreSQLInterval | null;
        min_age_at_first_period: PostGreSQLInterval | null;
        max_age_at_first_period: PostGreSQLInterval | null;
        require_permit_to_register: boolean;
        updated_by: {
          tenant_id: string;
          id: string;
          title: string | null;
          first_name: string | null;
          middle_name: string | null;
          last_name: string | null;
          nickname: string | null;
          email: string | null;
          date_of_birth: string | null;
          sex: string | null;
          active: boolean;
          created_at: string | null;
          updated_at: string | null;
          created_by: string | null;
          updated_by: string | null;
          des_id: string | null;
        } | null;
      }[]
    | null;
  course_self_schedule_criteria:
    | {
        tenant_id: string;
        course_id: string;
        updated_at: string;
        min_time_from_first_to_last: PostGreSQLInterval | null;
        max_time_from_first_to_last: PostGreSQLInterval | null;
        min_time_between_periods: PostGreSQLInterval | null;
        max_bookings_in_advance: number | null;
        max_bookings_in_interval: number | null;
        max_bookings_interval: PostGreSQLInterval | null;
        require_permit: boolean | null;
        first_period_max_balance: number | null;
        last_period_max_balance: number | null;
        other_max_balance: number | null;
        updated_by: {
          tenant_id: string;
          id: string;
          title: string | null;
          first_name: string | null;
          middle_name: string | null;
          last_name: string | null;
          nickname: string | null;
          email: string | null;
          date_of_birth: string | null;
          sex: string | null;
          active: boolean;
          created_at: string | null;
          updated_at: string | null;
          created_by: string | null;
          updated_by: string | null;
          des_id: string | null;
        } | null;
      }[]
    | null;
  course_completion_criteria:
    | {
        tenant_id: string;
        course_id: string;
        updated_at: string;
        performance_time_required: PostGreSQLInterval | null;
        observation_time_required: PostGreSQLInterval | null;
        updated_by: {
          tenant_id: string;
          id: string;
          title: string | null;
          first_name: string | null;
          middle_name: string | null;
          last_name: string | null;
          nickname: string | null;
          email: string | null;
          date_of_birth: string | null;
          sex: string | null;
          active: boolean;
          created_at: string | null;
          updated_at: string | null;
          created_by: string | null;
          updated_by: string | null;
          des_id: string | null;
        } | null;
      }[]
    | null;
}

async function getCourse(params: GetCourseParams): Promise<Course> {
  // if (params === undefined) {
  //     return [];
  // }

  // make api call
  const result = await privateApi.get<Course>(`${import.meta.env.VITE_API_BASE_URL}/courses/${params.id}`, {});

  // return the data object
  return result.data;
}

const useGetCourse = createQuery<Course, GetCourseParams, AxiosError<ErrorResponse>>({
  queryKey: ['course'],
  fetcher: (variables) => getCourse(variables),
});

export { getCourse, useGetCourse };
